import {useLazyQuery} from "@apollo/client";
import classNames from "classnames";
import pluralize from "pluralize";
import {ForwardedRef, forwardRef, useCallback, useContext, useMemo, useRef, useState} from "react";
import {FiChevronDown, FiChevronRight} from "react-icons/fi";
import {components, GroupBase, OptionProps, OptionsOrGroups, SelectInstance} from "react-select";
import {useAsyncPaginate, useComponents} from "react-select-async-paginate";

import {Button} from "@/components/button/Button.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {useStableLoadingState} from "@/components/select/useStableLoadingState.ts";
import {ThirdPartyComponentSelectContext} from "@/components/thirdPartyComponentSelect/ThirdPartyComponentSelectContext.tsx";
import {
  ThirdPartyDropdownIndicator,
  ThirdPartyIndicatorSeparator,
  ThirdPartyNoOptionsMessage,
  ThirdPartyValue,
} from "@/components/thirdPartyGroupSelect/ThirdPartyGroupSelect.tsx";
import {
  GQL_THIRD_PARTY_COMPONENTS_V2,
  ThirdPartyComponent,
  ThirdPartyComponentList,
  ThirdPartyService,
  ThirdPartySubComponent,
} from "@/lib/queries/checks.ts";

import styles from "./ThirdPartyComponentSelect.module.scss";
import groupStyles from "@/pages/checks/components/form/fields/components/ThirdPartyGroup.module.scss";

type ThirdPartyComponentSelectProps = SelectProps<ThirdPartyComponent> & {
  group: ThirdPartyService;
  excludeTitles?: string[];
  onSubComponentSelect?: (component: ThirdPartyComponent, subComponent: ThirdPartySubComponent) => void;
};

const ThirdPartyComponentOption = ({data, children, ...props}: OptionProps<ThirdPartyComponent>) => {
  const context = useContext(ThirdPartyComponentSelectContext);
  if (!context) {
    return null;
  }
  const {group, expandedId, onToggleExpand, onSubComponentSelect} = context;
  const isExpanded = expandedId === data.id;
  const hasMatchList = data.match_list && data.match_list.length > 0;
  return (
    <components.Option
      {...props}
      data={data}
      className={classNames(props.className, {"bg-transparent p-0": isExpanded && hasMatchList})}
    >
      <div
        className={classNames(styles.componentOption, {[styles.componentOptionExpanded]: isExpanded && hasMatchList})}
      >
        <div className="d-flex align-items-center flex-wrap row-gap-2">
          <CDNImage className={groupStyles.componentIconL} alt={`${group.group} logo`} src={group.logo_src} />
          <div className="pulseTextBodySmallMedium">{children}</div>
        </div>
        {(data.sub_title_count ?? 0) > 1 && (
          <div className="d-flex align-items-center">
            <Chip variant="tag" size="smallMedium">
              {pluralize("Component", data.match_count, true)}
            </Chip>
            {hasMatchList ? (
              <IconButton
                icon={isExpanded ? FiChevronDown : FiChevronRight}
                variant="ghost"
                size="fitIcon"
                className="ms-2"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleExpand(data.id);
                }}
              />
            ) : (
              <div className="ms-2 ps-4 pt-4" />
            )}
          </div>
        )}
      </div>
      {isExpanded && hasMatchList && (
        <>
          <div className="mt-2">
            {data.match_list!.map((subComponent) => (
              <Button
                key={subComponent.id}
                variant="selectSubOption"
                size="fitContent"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSubComponentSelect(data, subComponent);
                }}
              >
                <CDNImage className={groupStyles.componentIcon} alt={`${group.group} logo`} src={group.logo_src} />
                {subComponent.display_sub_title || subComponent.sub_title}
              </Button>
            ))}
          </div>
          <Divider className="mt-1 pb-1" />
        </>
      )}
    </components.Option>
  );
};

export const ThirdPartyComponentSelect = forwardRef(
  (
    {group, excludeTitles, onSubComponentSelect, ...props}: ThirdPartyComponentSelectProps,
    ref: ForwardedRef<SelectInstance<ThirdPartyComponent>>
  ) => {
    const selectRef = useRef<SelectInstance<ThirdPartyComponent> | null>(null);
    const setRef = useCallback(
      (node: SelectInstance<ThirdPartyComponent> | null) => {
        selectRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );
    const [getComponents] = useLazyQuery<ThirdPartyComponentList>(GQL_THIRD_PARTY_COMPONENTS_V2);
    const loadOptions = useCallback(
      async (search: string, prevOptions: OptionsOrGroups<ThirdPartyComponent, GroupBase<ThirdPartyComponent>>) => {
        const {data} = await getComponents({
          variables: {limit: 20, offset: prevOptions.length, group: group.id, title: search, excludeTitles},
        });
        const items = data?.cloudStatusComponents?.items || [];
        const total = data?.cloudStatusComponents?.total || 0;
        return {options: items, hasMore: prevOptions.length + items.length < total};
      },
      [getComponents, group.id, excludeTitles]
    );
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const handleToggleExpand = useCallback((id: string) => {
      setExpandedId((prev) => (prev === id ? null : id));
    }, []);
    const handleSubComponentSelect = useCallback(
      (component: ThirdPartyComponent, subComponent: ThirdPartySubComponent) => {
        onSubComponentSelect?.(component, subComponent);
        selectRef.current?.blur();
        setExpandedId(null);
      },
      [onSubComponentSelect]
    );
    const contextValue = useMemo(
      () => ({group, expandedId, onToggleExpand: handleToggleExpand, onSubComponentSelect: handleSubComponentSelect}),
      [group, expandedId, handleToggleExpand, handleSubComponentSelect]
    );
    const excludeTitlesKey = useMemo(() => excludeTitles?.join(",") || "", [excludeTitles]);
    const paginateResult = useStableLoadingState(
      useAsyncPaginate<ThirdPartyComponent, GroupBase<ThirdPartyComponent>, undefined>(
        {
          loadOptions,
          debounceTimeout: 500,
          onInputChange: (newValue, actionMeta) => {
            setExpandedId(null);
            props.onInputChange?.(newValue, actionMeta);
          },
        },
        [excludeTitlesKey]
      )
    );
    const paginateComponents = useComponents<ThirdPartyComponent, GroupBase<ThirdPartyComponent>, false>({
      Option: ThirdPartyComponentOption,
      SingleValue: ThirdPartyValue<ThirdPartyComponent>(props.placeholder),
      IndicatorSeparator: ThirdPartyIndicatorSeparator,
      DropdownIndicator: ThirdPartyDropdownIndicator<ThirdPartyComponent>,
      NoOptionsMessage: ThirdPartyNoOptionsMessage<ThirdPartyComponent>("component"),
    });
    return (
      <ThirdPartyComponentSelectContext.Provider value={contextValue}>
        <Select
          ref={setRef}
          getOptionValue={({id}) => id}
          getOptionLabel={({display_title, title}) => display_title || title || ""}
          isSearchable
          components={paginateComponents}
          {...props}
          {...paginateResult}
        />
      </ThirdPartyComponentSelectContext.Provider>
    );
  }
);
